import { createContext, useContext, useRef } from "react";
import type { CSSProperties, ReactNode } from "react";
import { cx } from "class-variance-authority";
import { AnimatePresence, motion } from "motion/react";
import type { VariantProps } from "class-variance-authority";

import { ButtonGroup } from "../ButtonGroup/ButtonGroup";
import { Button, type buttonVariants } from "../Button/Button";

import { getCurrencySymbol } from "./CurrencyInput.utils";
import * as styles from "./QuickInput.module.scss";

type QuickInputContextProps = {
  currencySymbol?: string;
  currency?: string;
} & Pick<VariantProps<typeof buttonVariants>, "variant" | "size">;

const QuickInputContext = createContext<QuickInputContextProps>({
  currencySymbol: "$",
  variant: "primary",
  size: "sm",
});

type ButtonVariants = VariantProps<typeof buttonVariants>;

type RootProps = {
  isOpen?: boolean;
  children?: ReactNode;
  currencySymbol?: string;
  currency?: string;
  arrowOffset?: string;
  /** Free-floating pill instead of a bar attached to the input above. */
  detached?: boolean;
  /** Renders a Clear pill at the bar's right end. */
  onClear?: () => void;
  variant?: ButtonVariants["variant"];
  size?: ButtonVariants["size"];
  className?: string;
  /** Called with the container once the open animation finishes. */
  onOpened?: (element: HTMLDivElement) => void;
};

const Root = ({
  isOpen,
  children,
  currencySymbol,
  currency,
  variant = "primary",
  size = "sm",
  arrowOffset,
  detached = false,
  onClear,
  className,
  onOpened,
}: RootProps) => {
  const containerRef = useRef<HTMLDivElement>(null);

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          ref={containerRef}
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: "auto" }}
          exit={{ opacity: 0, height: 0 }}
          transition={{
            opacity: { duration: 0.15, ease: "easeInOut" },
            height: { duration: 0.1, ease: "easeInOut" },
          }}
          onAnimationComplete={(definition) => {
            // Only the open animation targets height "auto".
            if (
              containerRef.current &&
              typeof definition === "object" &&
              "height" in definition &&
              definition.height === "auto"
            ) {
              onOpened?.(containerRef.current);
            }
          }}
          className={cx(
            styles.optionsContainer,
            {
              [styles.green]: variant === "primary",
              [styles.blurred]: variant === "blurred",
              [styles.detached]: detached,
              [styles.hasClear]: Boolean(onClear),
            },
            className,
          )}
          style={{ "--optionsArrowOffset": arrowOffset } as CSSProperties}
        >
          <QuickInputContext.Provider
            value={{ currencySymbol, currency, variant, size }}
          >
            <div className={styles.optionsContainerInner}>
              <ButtonGroup className={styles.optionButtonGroup}>
                {children}
              </ButtonGroup>
              {onClear && (
                <div className={styles.clearSlot}>
                  <button
                    type="button"
                    className={styles.clearButton}
                    onMouseDown={(event) => event.preventDefault()}
                    onClick={onClear}
                  >
                    Clear
                  </button>
                </div>
              )}
            </div>
          </QuickInputContext.Provider>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

type OptionProps = {
  value: number;
  label?: string;
  onClick: (value: number) => void;
};

const Option = ({ value, label, onClick }: OptionProps) => {
  const { currencySymbol, currency, variant, size } =
    useContext(QuickInputContext);

  return (
    <Button
      variant={variant}
      size={size}
      className={styles.optionButton}
      onMouseDown={(e) => e.preventDefault()}
      onClick={() => onClick(value)}
    >
      {label ||
        `+${currencySymbol || getCurrencySymbol(currency ?? "")}${value}`}
    </Button>
  );
};

type QuickInputNamespace = {
  Root: typeof Root;
  Option: typeof Option;
};

export const QuickInput = {
  Root,
  Option,
} satisfies QuickInputNamespace;
